import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Select, MenuItem, FormControl, FormHelperText,
  Button, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import CustomDatePicker from '../form-components/CustomDatePicker';
import DropdownTextField from '../form-components/DropdownTextField';
import { textFieldSx, selectFieldSx, dropdownMenuProps } from '../../utils/dropdownStyles';
import { useCurrency } from '../../context/CurrencyContext';
import LocalText from '../common/LocalText';
import { hasRequiredName, isDateRangeValid } from '../../utils/validators';


const EMPTY_FORM = {
  itemId:            '',
  nameLocalizations: [{ localeId: 'en_us', value: '' }],
  discountType:      'Percentage',
  value:             '',
  startDate:         '',
  endDate:           '',
};

// discountType string → discountTypeId int (matches discount_type table)
const TYPE_TO_ID = { Percentage: 1, Flat: 2 };
const ID_TO_TYPE = { 1: 'Percentage', 2: 'Flat' };

const DISC_STATUS_STYLE = {
  Active:   { bg: '#dcfce7', color: '#15803d' },
  Upcoming: { bg: '#dbeafe', color: '#1d4ed8' },
  Expired:  { bg: '#f1f5f9', color: '#64748b' },
};
const getDiscStatus = (d) => {
  const today = new Date().toISOString().slice(0, 10);
  const start = d.startAt ? String(d.startAt).slice(0, 10) : null;
  const end   = d.endAt   ? String(d.endAt).slice(0, 10)   : null;
  if (start && today < start) return 'Upcoming';
  if (end   && today > end)   return 'Expired';
  return 'Active';
};
const fmtDiscDate = (dt) => dt ? String(dt).slice(0, 10) : '—';

const computeDiscountedPrice = (item, discountType, value, factor) => {
  if (!item) return null;

  const baseCostMinor = item.prices?.[0]?.baseCost ?? 0;
  const price         = baseCostMinor / factor;

  const ratePct     = item.tax?.ratePct     ?? 0;
  const isInclusive = item.tax?.isInclusive ?? false;

  const taxAmt   = isInclusive
    ? price - price / (1 + ratePct / 100)
    : price * ratePct / 100;
  const subtotal = isInclusive ? price : price + taxAmt;

  const discountVal = parseFloat(value) || 0;
  const deduction   = discountType === 'Percentage'
    ? subtotal * discountVal / 100
    : discountVal;   // user enters major units for preview
  const finalPrice  = Math.max(0, subtotal - deduction);

  return { price, taxAmt, subtotal, deduction, finalPrice, ratePct, isInclusive };
};

// Shared label component — matches design: 10px bold uppercase slate-500
const FieldLabel = ({ children }) => (
  <Typography
    fontSize={10} fontWeight={700}
    sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.625, display: 'block' }}
  >
    <LocalText>{children}</LocalText>
  </Typography>
);

const inputSx = {
  ...textFieldSx,
  '& .MuiInputBase-input': { fontSize: 13, py: 1.125 },
};

const DiscountModal = ({ open, onClose, onSave, initialData, items, taxClasses, proposeMode = false, readOnly = false }) => {
  const isEdit = Boolean(initialData?.id);

  const { activeCurrency, currencies } = useCurrency();

  const [form,   setForm]   = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Resolve selected item first so its currency drives symbol/factor
  const selectedItem = useMemo(
    () => items.find(it => String(it.itemId) === String(form.itemId)) ?? null,
    [items, form.itemId]
  );

  const itemCurrencyId   = selectedItem?.prices?.[0]?.currencyId ?? null;
  const resolvedCurrency = (itemCurrencyId ? currencies.find(c => c.id === itemCurrencyId) : null) ?? activeCurrency;
  const decimalPlaces    = resolvedCurrency?.decimalPlaces ?? 2;
  const factor           = Math.pow(10, decimalPlaces);
  const symbol           = resolvedCurrency?.symbol ?? '';

  // Reset form when modal opens — compute factor inline from the edit item's
  // currency to avoid adding factor to deps (which would reset the form on item change)
  useEffect(() => {
    if (!open) return;
    if (initialData) {
      const initItem       = items.find(it => String(it.itemId) === String(initialData.itemId));
      const initCurrencyId = initItem?.prices?.[0]?.currencyId ?? null;
      const initCurrency   = (initCurrencyId ? currencies.find(c => c.id === initCurrencyId) : null) ?? activeCurrency;
      const initFactor     = Math.pow(10, initCurrency?.decimalPlaces ?? 2);
      const rawNames = Array.isArray(initialData.nameLocalizations)
        ? initialData.nameLocalizations
        : (initialData.nameLocalizations?.values ?? []);
      setForm({
        itemId:       initialData.itemId       != null ? String(initialData.itemId) : '',
        nameLocalizations: rawNames.length ? rawNames : [{ localeId: 'en_us', value: '' }],
        discountType: ID_TO_TYPE[initialData.discountTypeId] ?? 'Percentage',
        value:        initialData.discountTypeId === 2 && initialData.amount != null
                        ? String(initialData.amount / initFactor)
                        : initialData.ratePct   != null
                        ? String(initialData.ratePct)
                        : '',
        startDate:    initialData.startAt ? initialData.startAt.slice(0, 10) : '',
        endDate:      initialData.endAt   ? initialData.endAt.slice(0, 10)   : '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, initialData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  // ── Validity ──────────────────────────────────────────────────────────────
  const isNameValid    = hasRequiredName(form.nameLocalizations);
  const isItemValid    = isEdit || form.itemId !== '';
  const valueNum       = parseFloat(form.value);
  const isValueValid   = form.value !== '' && !Number.isNaN(valueNum) && valueNum >= 0 &&
    (form.discountType !== 'Percentage' || valueNum <= 100);
  const datesFilled    = form.startDate !== '' && form.endDate !== '';
  const isDateRangeOk  = !datesFilled || isDateRangeValid(form.startDate, form.endDate);
  const isValid = isNameValid && isItemValid && isValueValid && datesFilled && isDateRangeOk;

  const pricing = useMemo(
    () => computeDiscountedPrice(selectedItem, form.discountType, form.value, factor),
    [selectedItem, form.discountType, form.value, factor]
  );

  // Existing discount campaigns on the selected item
  const allDiscounts = selectedItem?.discounts ?? [];
  const activeDiscountsForPreview = allDiscounts
    .filter(d => getDiscStatus(d) === 'Active')
    .sort((a, b) => (a.discountTypeId ?? 0) - (b.discountTypeId ?? 0));

  // Yellow-box "After discounts" header — includes all active discounts (current state)
  let priceAfterDiscounts = pricing?.subtotal ?? 0;
  for (const d of activeDiscountsForPreview) {
    if (d.ratePct != null)
      priceAfterDiscounts = priceAfterDiscounts * (1 - d.ratePct / 100);
    else if (d.amount != null)
      priceAfterDiscounts = Math.max(0, priceAfterDiscounts - d.amount / factor);
  }

  // Preview calculation — exclude the discount being edited to avoid double-counting.
  // Embedded discounts from getItems() may not carry an `id` field, so fall back to
  // matching on type + value + schedule dates when id is unavailable on either side.
  const isSameAsEditing = (d) => {
    if (d.id != null && initialData?.id != null)
      return String(d.id) === String(initialData.id);
    const sameType  = d.discountTypeId === initialData?.discountTypeId;
    const sameValue = d.discountTypeId === 1
      ? d.ratePct === initialData?.ratePct
      : d.amount  === initialData?.amount;
    const sameStart = String(d.startAt ?? '').slice(0, 10) === String(initialData?.startAt ?? '').slice(0, 10);
    const sameEnd   = String(d.endAt   ?? '').slice(0, 10) === String(initialData?.endAt   ?? '').slice(0, 10);
    return sameType && sameValue && sameStart && sameEnd;
  };
  const otherActiveDiscounts = isEdit
    ? activeDiscountsForPreview.filter(d => !isSameAsEditing(d))
    : activeDiscountsForPreview;
  let priceAfterOtherDiscounts = pricing?.subtotal ?? 0;
  for (const d of otherActiveDiscounts) {
    if (d.ratePct != null)
      priceAfterOtherDiscounts = priceAfterOtherDiscounts * (1 - d.ratePct / 100);
    else if (d.amount != null)
      priceAfterOtherDiscounts = Math.max(0, priceAfterOtherDiscounts - d.amount / factor);
  }

  // Apply the new/updated discount ON TOP of the other active discounts
  const discountVal        = parseFloat(form.value) || 0;
  const newDeduction       = form.discountType === 'Percentage'
    ? priceAfterOtherDiscounts * discountVal / 100
    : discountVal;
  const finalCombinedPrice = Math.max(0, priceAfterOtherDiscounts - newDeduction);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || readOnly) return;
    setSaving(true);
    try {
      const discountTypeId = TYPE_TO_ID[form.discountType] ?? 1;
      const rawValue       = parseFloat(form.value) || 0;

      await onSave({
        id:                initialData?.id,
        itemId:            form.itemId ? Number(form.itemId) : undefined,
        discountTypeId,
        nameLocalizations: form.nameLocalizations.filter(l => l.value?.trim()),
        // type 1: ratePct as decimal; type 2: amount in minor units
        ratePct:           discountTypeId === 1 ? rawValue       : null,
        amount:            discountTypeId === 2 ? Math.round(rawValue * factor) : null,
        startAt:           form.startDate || undefined,
        endAt:             form.endDate   || undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  const getItemName = (it) => it.nameLocalizations?.values?.[0]?.value ?? it.name ?? '—';

  // Metrics grid data for the live calculation panel
  const metricsGrid = [
    { key: 'base',     label: 'Item Base', value: `${symbol}${pricing ? pricing.price.toFixed(decimalPlaces) : (0).toFixed(decimalPlaces)}`,    color: 'white'   },
    { key: 'tax',      label: `Tax (${pricing?.isInclusive ? 'incl' : 'excl'})`, value: `${symbol}${pricing ? pricing.taxAmt.toFixed(decimalPlaces) : (0).toFixed(decimalPlaces)}`, color: '#fbbf24' },
    { key: 'subtotal', label: 'Subtotal',  value: `${symbol}${pricing ? pricing.subtotal.toFixed(decimalPlaces) : (0).toFixed(decimalPlaces)}`,  color: '#93c5fd' },
    { key: 'discount', label: 'Discount',  value: `-${symbol}${newDeduction.toFixed(decimalPlaces)}`, color: '#f87171' },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px', overflow: 'hidden',
          border: '1px solid #e2e8f0', maxHeight: '92vh',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        },
      }}
    >
      {/* ── Light header with emerald accent bar ────────────────── */}
      <DialogTitle
        sx={{
          bgcolor: 'rgba(248,250,252,0.9)',
          borderBottom: '1px solid #f1f5f9',
          pt: 2.875, pb: 2.25, px: { xs: 2, sm: 3 },
          position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        {/* Gradient accent bar — emerald → teal → indigo */}
        <Box
          sx={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 3,
            background: '#10b981',
          }}
        />

        <Box display="flex" alignItems="center" gap={1.5}>
          {/* Icon box */}
          <Box
            sx={{
              width: 32, height: 32, borderRadius: '8px',
              bgcolor: '#ecfdf5', color: '#059669',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <EventOutlinedIcon sx={{ fontSize: 16 }} />
          </Box>
          <Box>
            <Typography fontWeight={800} fontSize={15} sx={{ color: '#1e293b', lineHeight: 1.3 }}>
              <LocalText>{readOnly ? 'View Discount Campaign' : isEdit ? 'Edit Discount Campaign' : 'Schedule Discount Campaign'}</LocalText>
            </Typography>
            <Typography fontSize={11} sx={{ color: '#94a3b8', mt: 0.2 }}>
              <LocalText>Establish dynamic date-sensitive price markdowns for specific products.</LocalText>
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{ color: '#94a3b8', '&:hover': { color: '#475569', bgcolor: 'rgba(100,116,139,0.1)' }, flexShrink: 0 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
        <DialogContent sx={{ p: { xs: 2, sm: 2.75 }, overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, ...(readOnly ? { pointerEvents: 'none', opacity: 0.82, userSelect: 'none' } : {}) }}>

          {/* ── Campaign Name (multi-locale) ──────────────────── */}
          <Box>
            <FieldLabel>Campaign Name *</FieldLabel>
            <DropdownTextField
              label="Campaign name"
              values={form.nameLocalizations}
              onChange={(locs) => setForm(prev => ({ ...prev, nameLocalizations: locs }))}
              attribute="value"
              required={!readOnly}
            />
          </Box>

          {/* ── Target Product ────────────────────────────────── */}
          <Box>
            <FieldLabel>{isEdit ? 'Target Product' : 'Target Product *'}</FieldLabel>
            <FormControl size="small" required={!isEdit} fullWidth>
              <Select
                value={form.itemId}
                onChange={handleChange('itemId')}
                displayEmpty
                disabled={isEdit}
                sx={{ ...selectFieldSx, '& .MuiSelect-select': { py: 1.125, fontSize: 13 } }}
                MenuProps={dropdownMenuProps}
              >
                <MenuItem value="" disabled><em style={{ fontSize: 13 }}><LocalText>Select a product…</LocalText></em></MenuItem>
                {items.map(it => (
                  <MenuItem key={it.itemId} value={String(it.itemId)}>
                    <Box>
                      <Typography fontSize={12.5} fontWeight={600} lineHeight={1.3}>{getItemName(it)}</Typography>
                      <Typography fontSize={10.5} sx={{ color: '#94a3b8', fontFamily: 'monospace' }}>{it.sku}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {!readOnly && !isEdit && !form.itemId && <FormHelperText><LocalText>Required</LocalText></FormHelperText>}
            </FormControl>
          </Box>

          {/* ── Existing Discount Campaigns on selected item ──── */}
          {selectedItem && allDiscounts.length > 0 && (
            <Box sx={{ border: '1px solid #fde68a', borderRadius: '12px', overflow: 'hidden' }}>
              <Box sx={{ bgcolor: '#fffbeb', px: 1.75, py: 1.125, borderBottom: '1px solid #fde68a', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography fontSize={9} fontWeight={800} sx={{ color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  <LocalText>Existing Campaigns</LocalText> ({allDiscounts.length})
                </Typography>
                {activeDiscountsForPreview.length > 0 && (
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <Typography fontSize={10} sx={{ color: '#64748b' }}><LocalText>After discounts:</LocalText></Typography>
                    <Typography fontSize={13} fontWeight={800} sx={{ color: '#15803d' }}>
                      {symbol}{priceAfterDiscounts.toFixed(decimalPlaces)}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box>
                {allDiscounts.map((d, i) => {
                  const status = getDiscStatus(d);
                  const style  = DISC_STATUS_STYLE[status];
                  const label  = d.discountTypeId === 1
                    ? `${d.ratePct}% Off`
                    : d.amount != null ? `${symbol}${(d.amount / factor).toFixed(decimalPlaces)} Flat` : '—';
                  return (
                    <Box
                      key={i}
                      sx={{
                        px: 1.75, py: 1.125,
                        borderBottom: i < allDiscounts.length - 1 ? '1px solid #fef3c7' : 'none',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap',
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{ px: 1.25, py: 0.25, borderRadius: '999px', bgcolor: style.bg, color: style.color, fontSize: 9, fontWeight: 800 }}>
                          <LocalText>{status}</LocalText>
                        </Box>
                        <Typography fontSize={12} fontWeight={700} sx={{ color: '#1e293b' }}>{label}</Typography>
                      </Box>
                      <Typography fontSize={10} sx={{ color: '#94a3b8', fontFamily: 'monospace' }}>
                        {fmtDiscDate(d.startAt)} → {fmtDiscDate(d.endAt)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* ── Discount Mode + Markdown Value ────────────────── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <FieldLabel>Discount Mode *</FieldLabel>
              <FormControl size="small" required fullWidth>
                <Select
                  value={form.discountType}
                  onChange={handleChange('discountType')}
                  sx={{ ...selectFieldSx, '& .MuiSelect-select': { py: 1.125, fontSize: 13 } }}
                  MenuProps={dropdownMenuProps}
                >
                  <MenuItem value="Percentage" sx={{ fontSize: 13 }}><LocalText>Percentage % Off</LocalText></MenuItem>
                  <MenuItem value="Flat"       sx={{ fontSize: 13 }}><LocalText>Flat Cash</LocalText> {symbol} <LocalText>Deducted</LocalText></MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box>
              <FieldLabel>
                {form.discountType === 'Percentage' ? 'Percentage (%) *' : `Amount (${symbol}) *`}
              </FieldLabel>
              <TextField
                hiddenLabel
                type="number"
                value={form.value}
                onChange={handleChange('value')}
                required
                size="small"
                fullWidth
                sx={inputSx}
                inputProps={{
                  min: 0,
                  max: form.discountType === 'Percentage' ? 100 : undefined,
                  step: form.discountType === 'Percentage' ? '0.01' : Math.pow(10, -decimalPlaces).toFixed(decimalPlaces),
                }}
                placeholder={form.discountType === 'Percentage' ? 'e.g. 15' : `e.g. 10.00`}
                helperText={
                  readOnly ? ''
                  : form.value === '' ? 'Required'
                  : valueNum < 0 ? 'Cannot be negative'
                  : form.discountType === 'Percentage' && valueNum > 100 ? 'Percentage cannot exceed 100'
                  : ''
                }
              />
            </Box>
          </Box>

          {/* ── Schedule Window ───────────────────────────────── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <FieldLabel>Schedule Starts At *</FieldLabel>
              <CustomDatePicker
                value={form.startDate}
                onChange={handleChange('startDate')}
                placeholder="Select start date"
                fullWidth
                isError={!readOnly && datesFilled && !isDateRangeOk}
                helperText={!readOnly && form.startDate === '' ? 'Required' : ''}
              />
            </Box>
            <Box>
              <FieldLabel>Schedule Ends At *</FieldLabel>
              <CustomDatePicker
                value={form.endDate}
                onChange={handleChange('endDate')}
                min={form.startDate || undefined}
                placeholder="Select end date"
                fullWidth
                isError={!readOnly && datesFilled && !isDateRangeOk}
                helperText={
                  readOnly ? ''
                  : form.endDate === '' ? 'Required'
                  : datesFilled && !isDateRangeOk ? 'Must be on or after start date'
                  : ''
                }
              />
            </Box>
          </Box>

          {/* ── Live Calculation Panel ────────────────────────── */}
          <Box
            sx={{
              background: '#1e1b4b',
              borderRadius: '12px', p: 2.25, color: 'white',
              overflow: 'hidden', position: 'relative',
            }}
          >
            {/* Radial accent */}
            <Box
              sx={{
                position: 'absolute', inset: 0,
                background: 'rgba(59,130,246,0.08)',
                pointerEvents: 'none',
              }}
            />

            {/* Header */}
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.75 }}>
              <Typography fontSize={9} fontWeight={800} sx={{ color: '#c7d2fe', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                <LocalText>Decoupled Campaign Calculations</LocalText>
              </Typography>
              <Box
                sx={{
                  bgcolor: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)',
                  px: 1.25, py: 0.3, borderRadius: '4px',
                  display: 'flex', alignItems: 'center', gap: 0.5,
                }}
              >
                <Typography fontSize={9} fontWeight={700} sx={{ color: '#86efac' }}><LocalText>Live Computation</LocalText></Typography>
              </Box>
            </Box>

            {/* Big final price */}
            <Box
              sx={{
                position: 'relative',
                display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                flexWrap: 'wrap', gap: 1,
                pb: 1.5, mb: 1.5, borderBottom: '1px solid rgba(99,102,241,0.3)',
              }}
            >
              <Typography fontSize={11.5} sx={{ color: '#c7d2fe' }}><LocalText>New Target Customer Price:</LocalText></Typography>
              <Typography fontSize={22} fontWeight={900} sx={{ color: '#34d399', letterSpacing: '-0.5px' }}>
                {symbol}{finalCombinedPrice.toFixed(decimalPlaces)}
              </Typography>
            </Box>

            {/* 4-column metrics grid */}
            <Box sx={{ position: 'relative', display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1 }}>
              {metricsGrid.map(({ key, label, value, color }) => (
                <Box
                  key={key}
                  sx={{
                    bgcolor: 'rgba(30,27,75,0.5)', border: '1px solid rgba(99,102,241,0.15)',
                    borderRadius: '6px', p: 1.25, textAlign: 'center',
                  }}
                >
                  <Typography fontSize={8} fontWeight={700} sx={{ color: '#818cf8', textTransform: 'uppercase', mb: 0.5 }}>
                    <LocalText>{label}</LocalText>
                  </Typography>
                  <Typography fontSize={12} fontWeight={700} sx={{ color, fontFamily: 'monospace' }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

        </Box>
        </DialogContent>

        {/* ── Actions ────────────────────────────────────────────── */}
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 }, pb: 2.75, pt: 1.5,
            borderTop: '1px solid #f1f5f9',
            gap: 1.5, flexShrink: 0,
          }}
        >
          <Button
            onClick={onClose}
            sx={{
              color: '#64748b', fontWeight: 600, fontSize: 12, textTransform: 'none',
              '&:hover': { color: '#475569', bgcolor: '#f8fafc' },
            }}
          >
            <LocalText>{readOnly ? 'Close' : 'Discard'}</LocalText>
          </Button>
          {!readOnly && (
            <Button
              type="submit"
              variant="contained"
              disabled={saving || !isValid}
              sx={{
                bgcolor: '#059669', fontWeight: 700, fontSize: 12, textTransform: 'none',
                borderRadius: '8px', px: 3, py: 1.125,
                boxShadow: '0 2px 8px rgba(5,150,105,0.35)',
                '&:hover': { bgcolor: '#047857', boxShadow: '0 4px 12px rgba(5,150,105,0.45)' },
                '&.Mui-disabled': { bgcolor: '#a7f3d0', color: 'white' },
              }}
            >
              <LocalText>{saving ? 'Submitting…' : proposeMode ? 'Save & Resubmit' : isEdit ? 'Update Campaign' : 'Apply Markdown Rules'}</LocalText>
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default DiscountModal;
